import { useEffect, useMemo, useRef, useState } from "react";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import AutoGrowTextarea from "@/shared/components/AutoGrowTextarea";
import {
    encodeArtwork, encodedSize, GMAIL_CLIP_BYTES, MAX_EMAIL_HTML,
} from "@/shared/utils/artworkTransport";

/*
    Authoring an email template.

    An email is an Ad like any other, so approval, rebranding and the content lock all work
    unchanged. What it carries is a subject, a preheader, the HTML body, and optionally a
    plain-text alternative.

    Three things are deliberately NOT here, and each one is a decision rather than an omission:

    The CAN-SPAM footer. The postal address, the unsubscribe link and the sender name are
    appended at send time. If they were editable an author could delete them, a copy-paste
    could lose them, and the first person to notice would work at a regulator.

    The plain-text part is optional. Left empty it is generated from the HTML at send time,
    because asking Ops to maintain two copies of the same copy is asking for them to drift.
    It is offered for the cases where the generated version reads badly.

    Any styling controls. The body is raw HTML on purpose: email templates come from
    designers as HTML, and a rich-text editor that rewrites them is a way to break careful
    table layouts that took somebody a day.
*/

interface EmailFormProps {
    ad: { id: number; name?: string | null };
    /** CreateEditorModal owns the content lock — see requestEdit there. */
    requestEdit: (run: (force: boolean) => void | Promise<void>) => void;
}

type Fields = {
    subject: string;
    preheader: string;
    html: string;
    text: string;
};

const EMPTY: Fields = { subject: "", preheader: "", html: "", text: "" };

const label: React.CSSProperties = {
    display: "block", fontSize: 11, color: "#6c7585", marginBottom: 3,
};
const input: React.CSSProperties = {
    width: "100%", border: "1px solid #d7dde8", borderRadius: 6, padding: "6px 8px",
    fontSize: 12, color: "#2d3648",
};

export default function EmailForm({ ad, requestEdit }: EmailFormProps) {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [fields, setFields] = useState<Fields>(EMPTY);
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;

    useEffect(() => {
        if (!open || loaded) return;
        ccService.getAd(ad.id, ["content"]).then((loadedAd) => {
            const content = (loadedAd.content ?? {}) as Record<string, any>;
            setFields({
                subject: content.subject ?? "",
                preheader: content.preheader ?? "",
                html: content.html ?? "",
                text: content.text ?? "",
            });
            setLoaded(true);
        }).catch(() => setLoaded(true));
    }, [open, loaded, ad.id, ccService]);

    const save = async (force: boolean) => {
        const latest = await ccService.getAd(ad.id, ["content"]);
        const current = (latest.content ?? {}) as Record<string, any>;
        const { html, ...rest } = fieldsRef.current;
        await ccService.updateAd(ad.id, {
            // The body goes encoded and the plain field is blanked. A WAF rule rejects CSS in
            // a request body before it reaches the API, and an email template is styled HTML
            // by definition (see artworkTransport).
            content: { ...current, ...rest, html: "", html_b64: encodeArtwork(html) } as any,
            force_edit: force,
        });
    };

    const size = encodedSize(fields.html);
    const tooBig = size > MAX_EMAIL_HTML;
    const willClip = size > GMAIL_CLIP_BYTES;

    const set = (patch: Partial<Fields>) => {
        const next = { ...fieldsRef.current, ...patch };
        fieldsRef.current = next;
        setFields(next);
    };

    return (
        <div style={{ marginTop: 8 }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    background: "none", border: "1px solid #d7dde8", borderRadius: 6,
                    padding: "5px 10px", fontSize: 12, color: "#4f596a", cursor: "pointer",
                    width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 6,
                }}
            >
                <span style={{ fontSize: 10, color: "#6c7585" }}>{open ? "v" : ">"}</span>
                Email
                <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8" }}>
                    {ad.name || `Ad ${ad.id}`}
                </span>
            </button>

            {open && (
                <div style={{
                    border: "1px solid #e1e6ef", borderTop: "none", borderRadius: "0 0 6px 6px",
                    padding: 10, display: "flex", flexDirection: "column", gap: 8, background: "#fff",
                }}>
                    {!loaded ? (
                        <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: 8 }}>
                            Loading...
                        </div>
                    ) : (
                        <>
                            <div>
                                <label style={label}>
                                    Subject
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        Required. {"{{first_name}}"} works here too.
                                    </span>
                                </label>
                                <input
                                    value={fields.subject}
                                    onChange={(e) => set({ subject: e.target.value })}
                                    onBlur={() => requestEdit(save)}
                                    style={input}
                                    aria-label="Email subject"
                                />
                            </div>

                            <div>
                                <label style={label}>
                                    Preheader
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        The grey line after the subject in an inbox list.
                                    </span>
                                </label>
                                <input
                                    value={fields.preheader}
                                    onChange={(e) => set({ preheader: e.target.value })}
                                    onBlur={() => requestEdit(save)}
                                    style={input}
                                    aria-label="Email preheader"
                                />
                            </div>

                            <div>
                                <label style={label}>
                                    Body
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        HTML. The unsubscribe footer is added automatically.
                                    </span>
                                </label>
                                <AutoGrowTextarea
                                    value={fields.html}
                                    onChange={(e) => set({ html: e.target.value })}
                                    onBlur={() => !tooBig && requestEdit(save)}
                                    rows={6}
                                    style={{
                                        ...input,
                                        borderColor: tooBig ? "#dc2626" : input.borderColor,
                                    }}
                                    aria-label="Email body"
                                />
                                {tooBig && (
                                    <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
                                        {size.toLocaleString()} bytes, and the most an email can
                                        carry is {MAX_EMAIL_HTML.toLocaleString()}. Nothing has
                                        been saved. Trim the template and it will save on the
                                        next edit.
                                    </div>
                                )}
                                {!tooBig && willClip && (
                                    <div style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>
                                        {size.toLocaleString()} bytes. Gmail stops showing a
                                        message past about {GMAIL_CLIP_BYTES.toLocaleString()}{" "}
                                        and hides the rest behind &quot;View entire
                                        message&quot;, which takes the unsubscribe footer with
                                        it. It will still send.
                                    </div>
                                )}
                            </div>

                            <div>
                                <label style={label}>
                                    Plain text
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        Optional. Generated from the body when left empty.
                                    </span>
                                </label>
                                <AutoGrowTextarea
                                    value={fields.text}
                                    onChange={(e) => set({ text: e.target.value })}
                                    onBlur={() => requestEdit(save)}
                                    rows={3}
                                    style={input}
                                    aria-label="Email plain text"
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
